// 1.3.98: 配额超限提示弹窗组件
// 用于存储超限、公开访问超限等场景的提示
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Crown, HardDrive, Link2, Trash2 } from 'lucide-react';

export type QuotaType = 'storage' | 'public_link';

interface QuotaExceededModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotaType: QuotaType;
  currentUsage?: number;
  maxQuota?: number;
  showUpgrade?: boolean;
  showDeleteHint?: boolean;
}

export function QuotaExceededModal({ 
  isOpen, 
  onClose, 
  quotaType,
  currentUsage,
  maxQuota,
  showUpgrade = true,
  showDeleteHint = false,
}: QuotaExceededModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate('/account');
  };

  if (!isOpen) return null;

  // 根据配额类型设置不同的内容
  const isStorage = quotaType === 'storage';
  
  const config = {
    storage: {
      icon: HardDrive,
      title: t('storageExceededTitle', '存储空间已满'),
      subtitle: t('storageExceededSubtitle', '请删除部分文档或升级套餐'),
      message: t('storageExceededMessage', '您的存储空间已达到上限，无法上传新文档。请删除一些不需要的文档或升级套餐以获得更多存储空间。'),
      usageLabel: t('currentStorageUsage', '当前存储'),
      usageFormat: (current: number, max: number) => `${current.toFixed(1)}GB / ${max}GB`,
      restrictions: [
        t('storageNoUpload', '无法上传新文档'),
        t('storageExistingOk', '已有文档正常使用'),
        t('storageDeleteOrUpgrade', '删除文档或升级套餐解除限制'),
      ],
    },
    public_link: {
      icon: Link2,
      title: t('publicLinkExceededTitle', '公开访问次数已达上限'),
      subtitle: t('publicLinkExceededSubtitle', '本月配额已用完'),
      message: t('publicLinkExceededMessage', '您的公开链接访问次数已达到本月上限。升级套餐可获得更多访问配额，或等待下月配额重置。'),
      usageLabel: t('currentPublicLinkUsage', '本月访问'),
      usageFormat: (current: number, max: number) => {
        const formatK = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n.toString();
        return `${formatK(current)} / ${formatK(max)}`;
      },
      restrictions: [
        t('publicLinkNoAccess', '公开链接暂时无法访问'),
        t('publicLinkInternalOk', '内部对话功能正常'),
        t('publicLinkResetNextMonth', '下月1日配额自动重置'),
      ],
    },
  };

  const currentConfig = config[quotaType];
  const IconComponent = currentConfig.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
          />
          {/* 弹窗内容 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* 头部 - YUI紫色主题背景 */}
              <div className="bg-gradient-to-r from-primary to-purple-600 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      {currentConfig.title}
                    </h2>
                    <p className="text-white/80 text-sm mt-1">
                      {currentConfig.subtitle}
                    </p>
                  </div>
                </div>
              </div>

              {/* 内容 */}
              <div className="p-6">
                {/* 用量显示 */}
                {currentUsage !== undefined && maxQuota !== undefined && (
                  <div className="bg-red-50 rounded-lg p-4 mb-4 border border-red-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-red-700 font-medium">{currentConfig.usageLabel}</span>
                      <span className="text-red-600 font-bold">
                        {currentConfig.usageFormat(currentUsage, maxQuota)}
                      </span>
                    </div>
                    <div className="w-full bg-red-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full"
                        style={{ width: '100%' }}
                      />
                    </div>
                    <p className="text-red-600 text-xs mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {t('quotaExceeded', '已超出配额限制')}
                    </p>
                  </div>
                )}

                <p className="text-gray-600 mb-4">
                  {currentConfig.message}
                </p>
                
                {/* 限制说明 */}
                <div className="bg-purple-50 rounded-lg p-4 mb-4 border border-purple-100">
                  <p className="text-purple-700 font-medium mb-2">
                    {t('currentRestrictions', '当前限制：')}
                  </p>
                  <ul className="text-sm text-purple-600 space-y-1">
                    {currentConfig.restrictions.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

                {/* 删除提示 */}
                {showDeleteHint && isStorage && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 flex items-start gap-2">
                    <Trash2 className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-700">
                      {t('deleteHint', '提示：您可以在项目详情中删除不需要的文档来释放存储空间。')}
                    </p>
                  </div>
                )}
              </div>

              {/* 底部按钮 */}
              <div className="flex items-center justify-end gap-3 p-6 pt-0">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('understood', '我知道了')}
                </button>
                {showUpgrade && (
                  <button
                    onClick={handleUpgrade}
                    className="px-6 py-2.5 text-white bg-gradient-to-r from-primary to-primary-dark rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                  >
                    <Crown className="w-4 h-4" />
                    {t('upgradeNow', '立即升级')}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * 1.3.98: 公开链接配额超限提示条
 * 用于在聊天界面顶部显示配额超限警告
 */
interface QuotaExceededBannerProps {
  quotaType: QuotaType;
  currentUsage?: number;
  maxQuota?: number;
}

export function QuotaExceededBanner({ quotaType, currentUsage, maxQuota }: QuotaExceededBannerProps) {
  const { t } = useTranslation();
  
  const isStorage = quotaType === 'storage';
  
  return (
    <div className="bg-red-50 border-b border-red-100 px-4 py-3">
      <div className="flex items-center gap-2 text-red-700">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium text-sm">
            {isStorage 
              ? t('storageExceededBanner', '存储空间已满')
              : t('publicLinkExceededBanner', '本月公开访问次数已达上限')
            }
          </p>
          {currentUsage !== undefined && maxQuota !== undefined && (
            <p className="text-xs text-red-600 mt-0.5">
              {isStorage 
                ? `${currentUsage.toFixed(1)}GB / ${maxQuota}GB`
                : `${currentUsage >= 1000 ? `${(currentUsage / 1000).toFixed(1)}K` : currentUsage} / ${maxQuota >= 1000 ? `${(maxQuota / 1000).toFixed(0)}K` : maxQuota}`
              }
            </p>
          )}
        </div>
      </div>
      <p className="text-xs text-red-600 mt-1">
        {isStorage 
          ? t('storageExceededHint', '请删除部分文档或升级套餐以继续上传')
          : t('publicLinkExceededHint', '公开链接暂时无法回复问题，请升级套餐或等待下月重置')
        }
      </p>
    </div>
  );
}
